import PropTypes from 'prop-types';
import React, { useImperativeHandle, forwardRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import ComingSoonModal from 'components/ComingSoonModal';
import DataTable, { CellPrimary, CellSecondary } from 'components/DataTable';
import useUserTable from '../hooks/useUserTable';

// Format phone number to (323) 842-1428 format
const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = ('' + phone).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[2]}) ${match[3]}-${match[4]}`;
  }
  return phone;
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const UserTable = forwardRef(({ searchTerm = '', roleFilter = '' }, ref) => {
  const location = useLocation();
  const history = useHistory();
  const { pathname } = location;

  // Determine route prefix based on current path
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const {
    users,
    loading,
    error,
    currentPage,
    setCurrentPage,
    totalPages,
    totalCount,
    appliedFilters,
  } = useUserTable(searchTerm, roleFilter);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getAppliedFilters: () => appliedFilters,
    getTotalCount: () => totalCount,
  }));

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Define table columns
  const columns = [
    {
      field: 'name',
      header: 'Name',
      render: (user) => (
        <>
          <CellPrimary hasSubtext>
            {user.last_name}, {user.first_name}
          </CellPrimary>
          <CellSecondary>
            {user.cellphone ? formatPhone(user.cellphone) : 'No phone'}
          </CellSecondary>
          <CellSecondary>
            {user.customer_since
              ? `Since ${user.customer_since}`
              : 'New customer'}
          </CellSecondary>
        </>
      ),
    },
    {
      field: 'events_attended_total',
      header: 'Events Attended',
      render: (user) => user.events_attended_total || 0,
    },
    {
      field: 'lifetime_value_total',
      header: 'Lifetime Value',
      render: (user) => formatCurrency(user.lifetime_value_total),
    },
    {
      field: 'last_purchase_date',
      header: 'Last Purchase',
      render: (user) => user.last_purchase_date || '—',
    },
  ];

  // Define table actions with wrapper support for ComingSoonModal
  const actions = [
    {
      type: 'message',
      title: 'Message',
      mobileLabel: 'Message',
      wrapper: ComingSoonModal,
      wrapperProps: {
        title: 'User Messaging',
        description:
          "We're building a powerful messaging system to help you connect with your users.",
        features: [
          'Message an individual user directly',
          'View history of all past conversations',
          'Get notifications and inbox for user messages',
        ],
      },
    },
    {
      type: 'view',
      title: 'View Details',
      mobileLabel: 'View Details',
      to: (user) => `${routePrefix}/users/transactions/${user.id}`,
    },
    {
      type: 'credit',
      title: 'Add Credit',
      mobileLabel: 'Add Credit',
      onClick: (user) =>
        history.push(`${routePrefix}/users/issue-credit/${user.id}`),
    },
    {
      type: 'edit',
      title: 'Edit User',
      mobileLabel: 'Edit',
      to: (user) => `${routePrefix}/users/edit/${user.id}`,
    },
  ];

  // Mobile quick info for compact display
  const mobileQuickInfo = [
    {
      field: 'events_attended_total',
      label: 'Events',
      render: (user) => user.events_attended_total || 0,
    },
    {
      field: 'lifetime_value_total',
      label: 'LTV',
      render: (user) => formatCurrency(user.lifetime_value_total),
    },
    {
      field: 'last_purchase_date',
      label: 'Last Purchase',
      render: (user) => user.last_purchase_date || '—',
    },
  ];

  // Empty state icon
  const emptyIcon = (
    <svg
      width="48"
      height="48"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      style={{ marginBottom: '16px', opacity: 0.5 }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );

  // Handle error state
  if (error) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '40px 0',
          color: '#EF4444',
        }}
      >
        Error loading users: {error.message}
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={users}
      loading={loading}
      keyField="id"
      emptyMessage="No users found"
      emptySubtext="Try adjusting your filters or search terms"
      emptyIcon={emptyIcon}
      actions={actions}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      skeletonRows={8}
      mobileQuickInfo={mobileQuickInfo}
    />
  );
});

UserTable.propTypes = {
  searchTerm: PropTypes.string,
  roleFilter: PropTypes.string,
};

export default UserTable;
